// 製單(工單)的型別與共用常數。後端:app/routers/work_orders.py。
// 狀態字樣跟著每一筆由後端送來(`status_label`);這裡的表只給篩選下拉與徽章顏色用,說法與後端同一套。

import { API_BASE } from "./client";
import type { BadgeTone } from "../components/Badge";

export type WorkOrderStatus = "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "SENT" | "COMPLETED" | "VOID";

export const WORK_ORDER_STATUSES: WorkOrderStatus[] = ["DRAFT", "PENDING_REVIEW", "APPROVED", "SENT", "COMPLETED", "VOID"];

export const WORK_ORDER_STATUS_LABEL: Record<WorkOrderStatus, string> = {
  DRAFT: "草稿",
  PENDING_REVIEW: "待覆核",
  APPROVED: "已覆核",
  SENT: "已寄出",
  COMPLETED: "已完成",
  VOID: "已作廢",
};

export const WORK_ORDER_STATUS_TONE: Record<WorkOrderStatus, BadgeTone> = {
  DRAFT: "neutral",
  PENDING_REVIEW: "warn",
  APPROVED: "ok",
  SENT: "accent",
  COMPLETED: "ok",
  VOID: "danger",
};

export type WorkOrderSize = "A4" | "B5" | "OTHER";
export const SIZES: WorkOrderSize[] = ["A4", "B5", "OTHER"];
export const SIZE_LABEL: Record<WorkOrderSize, string> = { A4: "A4", B5: "B5", OTHER: "其他" };

/** 勾了的一個選項,以及它的 +P / 頁數 / +文字。 */
export interface SpecPick {
  option: string;
  p?: number | null;
  pages?: number | null;
  text?: string | null;
}

/** `{ 欄位 key: 勾了的選項 }`;單選欄位最多一筆。 */
export type Spec = Record<string, SpecPick[]>;

export interface FormExtra {
  key: "p" | "pages" | "text";
  kind: "number" | "integer" | "text";
  name: string;
  prefix: string;
  suffix: string;
}

export interface FormOption {
  value: string;
  extras: FormExtra[];
}

export interface FormField {
  key: string;
  label: string;
  group: string;
  multi: boolean;
  options: FormOption[];
}

export interface FormDef {
  code: string;
  name: string;
  fields: FormField[];
}

export interface WorkOrderSummary {
  id: number;
  number: string;
  form_type: string;
  form_name: string;
  title: string;
  status: WorkOrderStatus;
  status_label: string;
  quote_id: number | null;
  /** 報價單在回收桶裡時是 null,`quote_deleted` 為 true。 */
  quote_no: string | null;
  quote_deleted: boolean;
  vendor_id: number | null;
  vendor_name: string;
  handler_user_id: number | null;
  handler_name: string;
  due_date: string | null;
  updated_at: string;
}

export interface WorkOrderHistoryEntry {
  action: string;
  label: string;
  user_name: string;
  at: string;
  note: string;
}

export interface WorkOrder extends WorkOrderSummary {
  issue_date: string | null;
  quantity: string;
  size: WorkOrderSize | null;
  size_other: string;
  note: string;
  spec: Spec;
  vendor_email: string;
  quote_line_id: number | null;
  created_by: number | null;
  created_by_name: string;
  reviewed_by: number | null;
  reviewer_name: string;
  reviewed_at: string | null;
  review_note: string;
  sent_at: string | null;
  sent_to: { to: string[]; cc: string[] } | null;
  void_at: string | null;
  created_at: string;
  can_review: boolean;
  other_owner_available: boolean;
  history: WorkOrderHistoryEntry[];
}

/** 新增(多帶 form_type)與 PUT 送的內容。**整份送**。 */
export interface WorkOrderInput {
  title: string;
  issue_date: string | null;
  due_date: string | null;
  quantity: string;
  size: WorkOrderSize | null;
  size_other: string;
  note: string;
  spec: Spec;
  vendor_id: number | null;
  quote_id: number | null;
  quote_line_id: number | null;
  handler_user_id: number | null;
}

export interface SendDefaults {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
}

export function workOrderExportUrl(id: number, kind: "pdf" | "docx"): string {
  return `${API_BASE}/work-orders/${id}/export.${kind}`;
}

export function inputOf(wo: WorkOrder): WorkOrderInput {
  return {
    title: wo.title, issue_date: wo.issue_date, due_date: wo.due_date, quantity: wo.quantity,
    size: wo.size, size_other: wo.size_other, note: wo.note, spec: wo.spec,
    vendor_id: wo.vendor_id, quote_id: wo.quote_id, quote_line_id: wo.quote_line_id,
    handler_user_id: wo.handler_user_id,
  };
}

/** 新單的初始值:發印日期 = 今天、承辦人 = 開單者(規格 §3.1)。 */
export function emptyInput(handlerId: number | null, today: string): WorkOrderInput {
  return {
    title: "", issue_date: today, due_date: null, quantity: "", size: null, size_other: "", note: "",
    spec: {}, vendor_id: null, quote_id: null, quote_line_id: null, handler_user_id: handlerId,
  };
}
